import { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { PermissionsTree } from '@/shared/types/api/roles';
import { collectLeafPaths } from '@/features/roles/modules/types';
import { cn } from '@/shared/utils/cn';

type Props = {
  tree: PermissionsTree;
  /** Set of dotted leaf paths currently checked. */
  value: Set<string>;
  onChange: (next: Set<string>) => void;
  /** When true, all checkboxes are forced checked and disabled. */
  readOnly?: boolean;
};

type CheckState = 'checked' | 'unchecked' | 'indeterminate';

type TreeNodeProps = {
  label: string;
  path: string; // dotted path for this node (empty for top-level "virtual" parent — not used)
  subtree: true | PermissionsTree;
  value: Set<string>;
  onToggleLeaf: (path: string, next: boolean) => void;
  onToggleGroup: (leaves: string[], allChecked: boolean) => void;
  readOnly: boolean;
  depth: number;
  defaultOpen?: boolean;
  i18nLabel: (path: string, fallback: string) => string;
};

const isLeaf = (subtree: true | PermissionsTree): subtree is true => subtree === true;

const groupState = (leaves: string[], value: Set<string>): CheckState => {
  if (leaves.length === 0) return 'unchecked';
  let checked = 0;
  for (const l of leaves) if (value.has(l)) checked += 1;
  if (checked === 0) return 'unchecked';
  if (checked === leaves.length) return 'checked';
  return 'indeterminate';
};

const Checkbox = ({
  state,
  disabled,
  onChange,
}: {
  state: CheckState;
  disabled?: boolean;
  onChange: (next: boolean) => void;
}) => {
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = state === 'indeterminate';
  }, [state]);
  return (
    <input
      ref={ref}
      type="checkbox"
      checked={state === 'checked'}
      disabled={disabled}
      onChange={(e) => onChange(e.target.checked)}
      onClick={(e) => e.stopPropagation()}
      className={cn(
        'h-4 w-4 cursor-pointer rounded border-slate-300 text-brand-600 transition-colors',
        'focus:ring-2 focus:ring-brand-200',
        disabled && 'cursor-not-allowed opacity-70',
      )}
      aria-checked={state === 'indeterminate' ? 'mixed' : state === 'checked'}
    />
  );
};

const TreeNode = ({
  label,
  path,
  subtree,
  value,
  onToggleLeaf,
  onToggleGroup,
  readOnly,
  depth,
  defaultOpen = false,
  i18nLabel,
}: TreeNodeProps) => {
  const [open, setOpen] = useState(defaultOpen);
  // Hooks must run before any conditional return (Rules of Hooks). Leaf nodes
  // have no descendant leaves to collect, so the memo short-circuits to [].
  const leaves = useMemo(
    () => (isLeaf(subtree) ? [] : collectLeafPaths(subtree, path)),
    [subtree, path],
  );

  if (isLeaf(subtree)) {
    const checked: CheckState = value.has(path) ? 'checked' : 'unchecked';
    return (
      <div
        className={cn(
          'flex items-center gap-2 rounded-md py-1.5 pr-2',
          'hover:bg-slate-50',
        )}
        style={{ paddingLeft: `${depth * 16 + 8}px` }}
      >
        <span className="inline-block h-4 w-4 shrink-0" aria-hidden />
        <Checkbox
          state={checked}
          disabled={readOnly}
          onChange={(next) => onToggleLeaf(path, next)}
        />
        <span className="text-sm text-ink-soft">{i18nLabel(path, label)}</span>
        <code className="ml-auto truncate text-[10px] text-ink-faint">{path}</code>
      </div>
    );
  }

  const state = groupState(leaves, value);

  return (
    <div>
      <div
        className={cn(
          'group flex cursor-pointer items-center gap-2 rounded-md py-1.5 pr-2 select-none',
          'hover:bg-slate-50',
        )}
        style={{ paddingLeft: `${depth * 16 + 4}px` }}
        onClick={() => setOpen((v) => !v)}
        role="button"
        aria-expanded={open}
      >
        <span className="inline-flex h-4 w-4 shrink-0 items-center justify-center text-ink-muted">
          {open ? (
            <ChevronDown className="h-4 w-4" />
          ) : (
            <ChevronRight className="h-4 w-4" />
          )}
        </span>
        <Checkbox
          state={state}
          disabled={readOnly}
          onChange={(next) => onToggleGroup(leaves, next)}
        />
        <span className="text-sm font-medium text-ink">{i18nLabel(path, label)}</span>
        <span className="ml-auto text-[11px] text-ink-faint">
          {leaves.filter((l) => value.has(l)).length}/{leaves.length}
        </span>
      </div>
      {open && (
        <div className="border-l border-slate-200" style={{ marginLeft: `${depth * 16 + 12}px` }}>
          {Object.entries(subtree).map(([key, child]) => {
            const childPath = path ? `${path}.${key}` : key;
            return (
              <TreeNode
                key={childPath}
                label={key}
                path={childPath}
                subtree={child}
                value={value}
                onToggleLeaf={onToggleLeaf}
                onToggleGroup={onToggleGroup}
                readOnly={readOnly}
                depth={depth + 1}
                i18nLabel={i18nLabel}
              />
            );
          })}
        </div>
      )}
    </div>
  );
};

export const PermissionsTreePicker = ({ tree, value, onChange, readOnly = false }: Props) => {
  const { t, i18n } = useTranslation();

  const allLeaves = useMemo(() => collectLeafPaths(tree), [tree]);

  // Effective value: super/read-only mode pretends everything is checked.
  const effective = readOnly ? new Set(allLeaves) : value;

  // Localized labels: try `roles.treePicker.nodes.<full.path>`, then
  // `roles.treePicker.nodes.<last-segment>`, else humanize fallback.
  const i18nLabel = (path: string, fallback: string): string => {
    if (!path) return fallback;
    const fullKey = `roles.treePicker.nodes.${path}`;
    if (i18n.exists(fullKey)) return t(fullKey);
    const last = path.split('.').slice(-1)[0] ?? fallback;
    const shortKey = `roles.treePicker.nodes.${last}`;
    if (i18n.exists(shortKey)) return t(shortKey);
    return last.replace(/_/g, ' ');
  };

  const handleToggleLeaf = (path: string, next: boolean): void => {
    const out = new Set(value);
    if (next) out.add(path);
    else out.delete(path);
    onChange(out);
  };

  const handleToggleGroup = (leaves: string[], allChecked: boolean): void => {
    const out = new Set(value);
    if (allChecked) for (const l of leaves) out.add(l);
    else for (const l of leaves) out.delete(l);
    onChange(out);
  };

  const handleSelectAll = (next: boolean): void => {
    handleToggleGroup(allLeaves, next);
  };

  const total = allLeaves.length;
  const checked = effective.size;
  const allOn = checked === total && total > 0;

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
        <div className="flex items-center gap-2 text-sm font-medium text-ink-soft">
          <Checkbox
            state={
              checked === 0 ? 'unchecked' : allOn ? 'checked' : 'indeterminate'
            }
            disabled={readOnly}
            onChange={handleSelectAll}
          />
          <span>{t('roles.treePicker.selectAll')}</span>
        </div>
        <span className="text-xs font-medium text-ink-muted">
          {t('roles.treePicker.selectedCount', { count: checked, total })}
        </span>
      </div>
      <div className="max-h-[60vh] overflow-y-auto rounded-lg border border-slate-200 bg-white p-1">
        {Object.entries(tree).map(([key, child]) => (
          <TreeNode
            key={key}
            label={key}
            path={key}
            subtree={child}
            value={effective}
            onToggleLeaf={handleToggleLeaf}
            onToggleGroup={handleToggleGroup}
            readOnly={readOnly}
            depth={0}
            defaultOpen
            i18nLabel={i18nLabel}
          />
        ))}
      </div>
    </div>
  );
};
